import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { loc } from '@/localization';
import type { ServiceError } from '@/services/apis/interfaces/ServiceError';
import { optionsService } from '@/services/instance/optionsService';
import { sso } from '@/services/instance/sso';
import { getRegistrationUrl, getRestoreUrl, getSelectSamlUrl } from '@/utils/getPageUrl';
import { required, loginPassword } from '@/utils/validationRules';

const validationRules = {
    email: required,
    password: loginPassword,
} as const;

type LoginRequest = Parameters<(typeof sso)['login']>[0];
type FormDataType = Omit<LoginRequest, 'csrfToken'>;

interface Params {
    onLogin?: (() => Promise<void>) | (() => void);
    onLoginFailed?: (() => Promise<void>) | (() => void);
}

export function useLoginPage(params: Params = {}) {
    const { onLogin, onLoginFailed } = params;

    const [searchParams] = useSearchParams();

    const initialEmail = searchParams.get('email') || undefined;

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        getValues,
        setValue,
        watch,
        trigger: triggerValidation,
        formState: { isSubmitting, errors },
    } = useForm<FormDataType>({
        defaultValues: {
            email: initialEmail,
        },
    });

    const maybeEmail = watch('email');

    const registrationUrl = getRegistrationUrl({
        ...optionsService.getQueryArgs(),
        email: maybeEmail,
    });

    const restoreUrl = getRestoreUrl({
        ...optionsService.getQueryArgs(),
        email: maybeEmail,
    });

    const selectSamlUrl = getSelectSamlUrl({
        ...optionsService.getQueryArgs(),
        email: maybeEmail,
    });

    const setUnathorizedErrors = useCallback(() => {
        setError('email', { type: 'value' });
        setError('password', {
            type: 'value',
            message: loc.Login.InvalidUsernameOrPassword,
        });
    }, [setError]);

    const setInvalidDataErrors = useCallback(
        (serviceError: ServiceError[] = []) => {
            const errorCodes = serviceError
                .map((e) => e.ErrorCode)
                .filter(Boolean)
                .join(', ');

            setError('email', { type: 'value' });
            setError('password', {
                type: 'value',
                message: `SomethingWentWrong$$${errorCodes}`,
            });
        },
        [setError],
    );

    const setNoInternetConnectionError = useCallback(() => {
        setError('password', {
            type: 'connection',
            message: loc.Login.NoInternetConnectionError,
        });
    }, [setError]);

    const setSomethingBadHappenedError = useCallback(() => {
        setError('password', {
            type: 'internal',
            message: loc.Login.InternalServerError,
        });
    }, [setError]);

    const cleanErrors = useCallback(() => {
        clearErrors(['email', 'password']);
    }, [clearErrors]);

    const passwordTrim = useCallback(() => {
        setValue('password', getValues().password.trim());
    }, [setValue, getValues]);

    const submitForm = useCallback(
        async (data: FormDataType) => {
            const formValid = await triggerValidation();

            if (!formValid) {
                return;
            }

            passwordTrim();
            if (navigator && !navigator.onLine) {
                setNoInternetConnectionError();
                await onLoginFailed?.();
                return;
            }

            try {
                const response = await sso.login({
                    ...data,
                    password: data.password.trim(),
                });

                if (!response.Success) {
                    const hasUnathorizedError = response.Errors.some(
                        (err) => err.ErrorCode === 'Unathorized' || err.ErrorCode === 'ObjectNotFound',
                    );

                    if (hasUnathorizedError) {
                        setUnathorizedErrors();
                    } else {
                        setInvalidDataErrors(response.Errors);
                    }

                    await onLoginFailed?.();
                    return;
                }

                await onLogin?.();
                await sso.return({
                    ...response,
                    IsNewUserCreated: false,
                });
            } catch {
                setSomethingBadHappenedError();
                await onLoginFailed?.();
            }
        },
        [
            onLogin,
            onLoginFailed,
            passwordTrim,
            setUnathorizedErrors,
            setInvalidDataErrors,
            setNoInternetConnectionError,
            setSomethingBadHappenedError,
            triggerValidation,
        ],
    );

    const onFormSubmit = useCallback(
        (ev: React.BaseSyntheticEvent) => {
            void handleSubmit(submitForm, passwordTrim)(ev);
        },
        [handleSubmit, passwordTrim, submitForm],
    );

    useEffect(() => {
        if (errors.password === undefined && errors.email?.type === 'required') {
            setError('password', {
                type: 'value',
                message: loc.Login.InvalidUsernameOrPassword,
            });
        }
    }, [errors.email?.type, errors.password, setError]);

    return {
        setInvalidDataErrors,
        registrationUrl,
        restoreUrl,
        selectSamlUrl,
        register,
        errors,
        cleanErrors,
        onFormSubmit,
        isSubmitting,
        getValues,
        validationRules,
    };
}
